import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const priorityColors = {
  high: "bg-[#FEF3F2] text-[#B42318] border-[#D92D20]",
  medium: "bg-[#FFFAEB] text-[#B54708] border-[#F79009]",
  low: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

const statusCols = [
  { key: "todo", label: "To Do" },
  { key: "in_progress", label: "In Progress" },
  { key: "done", label: "Done" },
];

const empty = { title: "", description: "", client_id: "", assigned_to: "", priority: "medium", status: "todo", due_date: "" };

export default function Tasks() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [a, b, c] = await Promise.all([api.get("/tasks"), api.get("/clients"), api.get("/users")]);
    setItems(a.data);
    setClients(b.data);
    setTeam(c.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...empty, ...t, client_id: t.client_id || "", assigned_to: t.assigned_to || "", due_date: t.due_date || "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        client_id: form.client_id || null,
        assigned_to: form.assigned_to || null,
        due_date: form.due_date || null,
      };
      if (editing) await api.put(`/tasks/${editing.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success("Task saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete task?")) return;
    await api.delete(`/tasks/${id}`);
    load();
  };

  const setStatus = async (t, status) => {
    await api.put(`/tasks/${t.id}`, {
      title: t.title, description: t.description, client_id: t.client_id,
      assigned_to: t.assigned_to, priority: t.priority, status, due_date: t.due_date,
    });
    load();
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-caps mb-2">Operations</p>
          <h1 className="font-display font-black text-4xl tracking-tighter">Tasks</h1>
          <p className="text-zinc-600 mt-2">Assign work to team and track progress.</p>
        </div>
        {can(user, "tasks_write") && (
          <button onClick={openNew} data-testid="new-task" className="bg-zinc-900 text-white px-4 py-2.5 font-semibold flex items-center gap-2 hover:bg-zinc-800">
            <Plus size={16} /> New Task
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statusCols.map(col => (
          <div key={col.key} className="bg-white border border-zinc-200" data-testid={`column-${col.key}`}>
            <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
              <div className="label-caps">{col.label}</div>
              <span className="text-xs font-mono text-zinc-500">{items.filter(t => t.status === col.key).length}</span>
            </div>
            <div className="p-3 space-y-2 min-h-[200px]">
              {items.filter(t => t.status === col.key).map(t => (
                <div key={t.id} className="border border-zinc-200 p-3 hover:border-zinc-400 transition-colors group">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="font-semibold text-sm leading-snug">{t.title}</div>
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-medium border uppercase ${priorityColors[t.priority]}`}>{t.priority}</span>
                  </div>
                  {t.description && <div className="text-xs text-zinc-600 mb-2 line-clamp-2">{t.description}</div>}
                  <div className="text-xs text-zinc-500 space-y-0.5">
                    {t.client_name && <div>Client: <span className="font-medium text-zinc-700">{t.client_name}</span></div>}
                    {t.assigned_to_name && <div>To: <span className="font-medium text-zinc-700">{t.assigned_to_name}</span></div>}
                    {t.due_date && <div>Due: <span className="font-mono">{t.due_date}</span></div>}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-zinc-100">
                    <select value={t.status} onChange={e => setStatus(t, e.target.value)} className="text-xs border border-zinc-300 px-2 py-1 bg-white" disabled={!can(user, "tasks_write")}>
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="done">Done</option>
                    </select>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      {can(user, "tasks_write") && <button onClick={() => openEdit(t)} className="p-1 hover:bg-zinc-100"><Pencil size={12} /></button>}
                      {can(user, "delete") && <button onClick={() => remove(t.id)} className="p-1 hover:bg-red-50 text-[#D92D20]"><Trash2 size={12} /></button>}
                    </div>
                  </div>
                </div>
              ))}
              {items.filter(t => t.status === col.key).length === 0 && (
                <div className="text-center py-8 text-xs text-zinc-400 font-mono">[ empty ]</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-zinc-200 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl tracking-tight">{editing ? "Edit Task" : "New Task"}</h2>
              <button onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="label-caps block mb-2">Title *</label>
                <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="task-title" className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
              </div>
              <div>
                <label className="label-caps block mb-2">Description</label>
                <textarea rows="3" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps block mb-2">Client</label>
                  <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                    <option value="">None</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-caps block mb-2">Assign to</label>
                  <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                    <option value="">Unassigned</option>
                    {team.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label-caps block mb-2">Priority</label>
                  <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="label-caps block mb-2">Status</label>
                  <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 bg-white">
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="done">Done</option>
                  </select>
                </div>
                <div>
                  <label className="label-caps block mb-2">Due</label>
                  <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 font-semibold">Cancel</button>
                <button type="submit" data-testid="save-task" className="bg-zinc-900 text-white px-4 py-2 font-semibold">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
